import {
  abortMultipart,
  completeMultipart,
  completeUpload,
  createUpload,
  getUploadParts,
  getUploadPlan,
} from '../api/confetti'
import type { CreateUploadResult, PresignedPart, UploadTarget } from '../api/types'
import {
  deleteItem,
  listItems,
  putItem,
  type QueueItem,
  type QueueStatus,
  type UploadPlan,
  type UploadedPart,
} from './db'
import { kindOf, prepare } from './prepare'
import { UploadAborted, putToStorage } from './transport'

/** Twee tegelijk. Meer is op feest-wifi vooral meer kans dat er één omvalt. */
const maxConcurrent = 2

/** Daarna staat het item op "mislukt" en wacht het op een tik van de gast. */
const maxAttempts = 5

const defaultBackoffMs = 2_000
const maxBackoffMs = 60_000

/** Zoveel part-URLs deelt de server per antwoord uit. */
const partsPerBatch = 10

/**
 * Marge op het vervalmoment van de presigned URLs. Een handtekening die over
 * veertig seconden verloopt is voor een part van 10 MB net zo goed als verlopen.
 */
const freshnessMarginMs = 60_000

export interface QueueContext {
  slug: string
  guestId: string
}

export interface QueueOptions {
  /**
   * De eerste wachttijd tussen twee pogingen; daarna verdubbelt hij tot een
   * minuut. Instelbaar zodat een test de vijf pogingen kan aflopen zonder er
   * een halve minuut op te wachten.
   */
  baseBackoffMs?: number
}

/**
 * De persistente upload-wachtrij.
 *
 * Eén instantie voor de hele app, want de wachtrij hoort bij het tabblad en
 * niet bij een scherm: je kan hem openen vanuit het menu, vanuit de galerij,
 * of helemaal niet, en hij loopt gewoon door.
 *
 * De waarheid staat in IndexedDB, niet in het geheugen. Elke overgang wordt
 * eerst weggeschreven en dan pas doorgegeven aan de UI — sluit iemand de tab
 * midden in een upload, dan vindt de volgende sessie precies terug waar het
 * gebleven was, en bij een groot bestand vanaf het laatste geslaagde part.
 */
export class UploadQueue {
  private items = new Map<string, QueueItem>()
  private snapshot: QueueItem[] = []
  private readonly listeners = new Set<() => void>()
  private readonly running = new Map<string, AbortController>()
  private readonly timers = new Set<ReturnType<typeof setTimeout>>()

  private context: QueueContext | null = null
  private loadedSlug: string | null = null

  private readonly baseBackoffMs: number

  constructor(options: QueueOptions = {}) {
    this.baseBackoffMs = options.baseBackoffMs ?? defaultBackoffMs
  }

  /** Voortschrijdend gemiddelde in bytes/seconde, voor "nog ± 40 sec". */
  private rate: number | null = null
  private lastSample: { at: number; bytes: number } | null = null

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener)

    return () => this.listeners.delete(listener)
  }

  getSnapshot = (): QueueItem[] => this.snapshot

  /** Bytes per seconde over de laatste metingen, of null als we niets weten. */
  getRate = (): number | null => this.rate

  /**
   * Koppelt de wachtrij aan een feest en een gast, leest wat er nog ligt, en
   * begint te pompen. Idempotent: elke render mag dit aanroepen.
   */
  async activate(context: QueueContext): Promise<void> {
    this.context = context

    if (this.loadedSlug === context.slug) {
      this.pump()

      return
    }

    this.loadedSlug = context.slug

    for (const item of await listItems(context.slug)) {
      // Een item dat "bezig" stond toen de tab dichtging is dat nu niet meer.
      // Terug in de rij, zodat hij vanzelf verder gaat — met de parts die al
      // gelukt waren nog gewoon in dit record.
      //
      // De backoff gaat er ook af. Die hoort bij een reeks pogingen binnen één
      // sessie; een tab die net weer opengaat hoort niet eerst een minuut te
      // wachten omdat de vorige poging een uur geleden misging. Alleen wat
      // definitief mislukt is blijft staan: daar hoort een tik op "Opnieuw" bij.
      const interrupted =
        item.status === 'uploading' ||
        item.status === 'preparing' ||
        (item.status === 'queued' && item.nextAttemptAt > 0)

      const revived = interrupted
        ? { ...item, status: 'queued' as QueueStatus, progress: 0, nextAttemptAt: 0 }
        : item

      this.items.set(revived.id, revived)

      if (revived !== item) {
        await putItem(revived)
      }
    }

    this.publish()
    this.pump()
  }

  /** Voegt gekozen bestanden toe. De voorbereiding gebeurt in de wachtrij zelf. */
  async add(files: File[], challengeId: string | null = null): Promise<void> {
    const context = this.context

    if (!context) {
      throw new Error('De wachtrij weet nog niet bij welk feest hij hoort.')
    }

    // Een nieuwe keuze begint met een schone teller. Zonder dit blijft de
    // banner "2 van 2 geüpload" van een uur geleden meetellen en gaat de
    // voortgang van deze acht foto's meteen op 20% van start.
    if (!this.snapshot.some((item) => item.status !== 'done')) {
      await this.forgetDone()
    }

    const now = Date.now()

    for (const [index, file] of files.entries()) {
      const item: QueueItem = {
        id: crypto.randomUUID(),
        slug: context.slug,
        guestId: context.guestId,
        challengeId,
        fileName: file.name || 'foto.jpg',
        contentType: file.type || 'image/jpeg',
        sizeBytes: file.size,
        kind: kindOf(file),
        original: file,
        status: 'queued',
        progress: 0,
        attempts: 0,
        nextAttemptAt: 0,

        // De index erbij, anders krijgen bestanden uit dezelfde keuze
        // dezelfde tijdstempel en is hun volgorde willekeurig.
        createdAt: now + index,
      }

      this.items.set(item.id, item)
      await putItem(item)
    }

    this.publish()
    this.pump()
  }

  /**
   * "Opnieuw" op één mislukte upload.
   *
   * Geeft een promise terug die klaar is zodra het item weer in de rij staat.
   * De UI hoeft daar niet op te wachten — hij ziet het via de snapshot — maar
   * zonder die promise is er geen moment waarop je weet dat het gebeurd is.
   */
  async retry(id: string): Promise<void> {
    const item = this.items.get(id)

    if (item && item.status === 'failed') {
      await this.update(id, {
        status: 'queued',
        attempts: 0,
        nextAttemptAt: 0,
        error: undefined,
      })
    }
  }

  /** "Alles opnieuw" — achteraan de wachtrij, zoals de bevestiging belooft. */
  async retryAll(): Promise<void> {
    for (const item of this.snapshot) {
      if (item.status === 'failed') {
        await this.retry(item.id)
      }
    }
  }

  /** Het kruisje op een item: breekt af als hij bezig is, en gooit hem weg. */
  async cancel(id: string): Promise<void> {
    const item = this.items.get(id)

    this.running.get(id)?.abort()
    this.running.delete(id)

    this.items.delete(id)
    await deleteItem(id)

    this.publish()

    // De parts die al in de opslag staan zijn geen object en zijn met een
    // HeadObject niet te vinden; ze kosten wel geld tot ze expliciet
    // afgebroken worden. Dit mag mislukken: de opruimjob vindt hem later ook.
    if (item?.mediaItemId && item.plan?.uploadId) {
      try {
        await abortMultipart(item.slug, item.mediaItemId)
      } catch {
        // Bewust stil; zie hierboven.
      }
    }

    this.pump()
  }

  /** "Wachtrij leegmaken": alles wat nog niet begonnen is. */
  async clearQueued(): Promise<void> {
    for (const item of this.snapshot) {
      if (item.status === 'queued' && !this.running.has(item.id)) {
        await this.cancel(item.id)
      }
    }
  }

  /**
   * Ruimt afgeronde items op. Ze staan in de galerij, dus ze twee keer laten
   * zien voegt niets toe zodra de galerij ze opgehaald heeft.
   */
  async forgetDone(): Promise<void> {
    for (const item of this.snapshot) {
      if (item.status === 'done') {
        this.items.delete(item.id)
        await deleteItem(item.id)
      }
    }

    this.publish()
  }

  private publish(): void {
    this.snapshot = [...this.items.values()].sort((a, b) => a.createdAt - b.createdAt)

    for (const listener of this.listeners) {
      listener()
    }
  }

  private async update(id: string, patch: Partial<QueueItem>): Promise<QueueItem | null> {
    const current = this.items.get(id)

    if (!current) {
      return null
    }

    const next = { ...current, ...patch }

    this.items.set(id, next)
    await putItem(next)

    this.publish()

    if (patch.status === 'queued') {
      this.pump()
    }

    return next
  }

  /**
   * Alleen de voortgang. Gaat bewust níét naar IndexedDB: dat zou tientallen
   * schrijfacties per seconde per bestand opleveren, en na een herstart begint
   * een upload toch weer bij het begin van zijn huidige part.
   */
  private progress(id: string, value: number): void {
    const current = this.items.get(id)

    if (current) {
      this.items.set(id, { ...current, progress: Math.min(0.999, value) })
      this.publish()
    }
  }

  private pump(): void {
    if (!this.context) {
      return
    }

    const now = Date.now()

    for (const item of this.snapshot) {
      if (this.running.size >= maxConcurrent) {
        return
      }

      if (item.status !== 'queued' || this.running.has(item.id)) {
        continue
      }

      if (item.nextAttemptAt > now) {
        this.wake(item.nextAttemptAt - now)

        continue
      }

      void this.run(item.id)
    }
  }

  private wake(delay: number): void {
    const timer = setTimeout(() => {
      this.timers.delete(timer)
      this.pump()
    }, delay)

    this.timers.add(timer)
  }

  private async run(id: string): Promise<void> {
    const controller = new AbortController()

    this.running.set(id, controller)

    try {
      await this.upload(id, controller.signal)

      await this.update(id, { status: 'done', progress: 1, error: undefined })
    } catch (cause) {
      if (cause instanceof UploadAborted || controller.signal.aborted) {
        // Afgebroken door de gast; `cancel` heeft het item al weggegooid.
        return
      }

      await this.fail(id, cause)
    } finally {
      this.running.delete(id)
      this.pump()
    }
  }

  private async fail(id: string, cause: unknown): Promise<void> {
    const item = this.items.get(id)

    if (!item) {
      return
    }

    const attempts = item.attempts + 1
    const message = cause instanceof Error ? cause.message : 'Er ging iets mis.'

    if (attempts >= maxAttempts) {
      await this.update(id, { status: 'failed', attempts, error: message })

      return
    }

    await this.update(id, {
      status: 'queued',
      attempts,
      error: message,
      nextAttemptAt: Date.now() + Math.min(maxBackoffMs, this.baseBackoffMs * 2 ** (attempts - 1)),
    })
  }

  private async upload(id: string, signal: AbortSignal): Promise<void> {
    await this.ensurePrepared(id)
    await this.update(id, { status: 'uploading' })

    const plan = await this.ensurePlan(id)

    if (plan.mode === 'Multipart') {
      await this.uploadInParts(id, signal)
    } else {
      await this.uploadOriginal(id, plan.original!, signal)
    }

    await this.uploadWeb(id, signal)

    const item = this.items.get(id)!

    await completeUpload(item.slug, item.mediaItemId!, {
      width: item.width,
      height: item.height,
      durationSeconds: item.durationSeconds,
    })
  }

  /** Tekent het bestand op canvas en bewaart de webversie en het miniatuur. */
  private async ensurePrepared(id: string): Promise<void> {
    const item = this.items.get(id)!

    if (item.web) {
      return
    }

    await this.update(id, { status: 'preparing' })

    const prepared = await prepare(
      new File([item.original], item.fileName, { type: item.contentType }),
    )

    await this.update(id, {
      web: prepared.web,
      poster: prepared.poster,
      width: prepared.width,
      height: prepared.height,
      durationSeconds: prepared.durationSeconds,
    })
  }

  /**
   * Zorgt voor een media-item met bruikbare handtekeningen.
   *
   * Bestaat het item nog niet, dan wordt het aangemaakt. Bestaat het wel maar
   * zijn de handtekeningen verlopen, dan worden alleen díé ververst — een
   * nieuwe upload aanvragen zou bij multipart een nieuwe uploadId opleveren en
   * daarmee alle parts weggooien die al gelukt waren.
   */
  private async ensurePlan(id: string): Promise<UploadPlan> {
    const item = this.items.get(id)!

    if (item.mediaItemId && item.plan && this.fresh(item.plan)) {
      return item.plan
    }

    const result: CreateUploadResult = item.mediaItemId
      ? await getUploadPlan(item.slug, item.mediaItemId)
      : await createUpload(item.slug, {
          guestId: item.guestId,
          fileName: item.fileName,
          contentType: item.contentType,
          sizeBytes: item.sizeBytes,
          kind: item.kind,
          challengeId: item.challengeId,
          width: item.width,
          height: item.height,
          durationSeconds: item.durationSeconds,
        })

    const plan: UploadPlan = {
      mode: result.originalMode,
      expiresAtUtc: result.expiresAtUtc,
      original: result.original ?? undefined,
      web: result.web,
      uploadId: result.multipart?.uploadId,
      partSizeBytes: result.multipart?.partSizeBytes,
      partCount: result.multipart?.partCount,
    }

    await this.update(id, { mediaItemId: result.mediaItemId, plan })

    return plan
  }

  private fresh(plan: UploadPlan): boolean {
    return new Date(plan.expiresAtUtc).getTime() - Date.now() > freshnessMarginMs
  }

  private async uploadOriginal(
    id: string,
    target: UploadTarget,
    signal: AbortSignal,
  ): Promise<void> {
    const item = this.items.get(id)!
    const total = item.sizeBytes + (item.web?.size ?? 0)

    await putToStorage(target.url, item.original, target.headers, {
      signal,
      onProgress: (bytes) => {
        this.sample(bytes)
        this.progress(id, bytes / total)
      },
    })
  }

  /**
   * Het multipart-pad. Snijdt het bestand in stukken van de partgrootte die de
   * server heeft opgegeven en werkt de reeksen af.
   *
   * Na élk geslaagd part gaat de ETag naar IndexedDB. Dat is de enige reden dat
   * een hervatte upload van een video van 500 MB niet weer bij nul begint.
   */
  private async uploadInParts(id: string, signal: AbortSignal): Promise<void> {
    let item = this.items.get(id)!

    const partSize = item.plan!.partSizeBytes!
    const partCount = item.plan!.partCount!
    const total = item.sizeBytes + (item.web?.size ?? 0)
    const fileSize = item.original.size

    const done = new Map((item.uploadedParts ?? []).map((part) => [part.partNumber, part]))

    let next = 1

    while (next <= partCount) {
      if (done.has(next)) {
        next += 1

        continue
      }

      const batch = await this.partBatch(id, next)

      if (batch.length === 0) {
        break
      }

      for (const part of batch) {
        if (done.has(part.partNumber)) {
          continue
        }

        const start = (part.partNumber - 1) * partSize
        const chunk = item.original.slice(start, Math.min(start + partSize, fileSize))

        // Kan gebeuren als het bestand kleiner blijkt dan waar het aantal parts
        // op berekend is. Een leeg part accepteert de opslag niet, en het is
        // ook nergens voor nodig.
        if (chunk.size === 0) {
          next = partCount + 1

          break
        }

        const already = [...done.values()].reduce(
          (sum, entry) => sum + partBytes(entry.partNumber, partSize, fileSize),
          0,
        )

        const { etag } = await putToStorage(
          part.url,
          chunk,
          {},
          {
            signal,
            onProgress: (bytes) => {
              this.sample(bytes)
              this.progress(id, (already + bytes) / total)
            },
          },
        )

        if (!etag) {
          throw new Error('De opslag gaf geen ETag terug voor dit deel.')
        }

        done.set(part.partNumber, { partNumber: part.partNumber, eTag: etag })

        item = (await this.update(id, { uploadedParts: [...done.values()] })) ?? item

        next = part.partNumber + 1
      }
    }

    const parts: UploadedPart[] = [...done.values()].sort((a, b) => a.partNumber - b.partNumber)

    await completeMultipart(item.slug, item.mediaItemId!, parts)

    // De multipart bestaat niet meer; hem hier laten staan zou `cancel` later
    // een afbreek-verzoek laten sturen voor een upload die al af is.
    await this.update(id, { plan: { ...item.plan!, uploadId: undefined } })
  }

  /**
   * Haalt een reeks part-URLs op. De eerste reeks uit het aanmaak-antwoord
   * wordt niet bewaard — hij verloopt net zo hard als de rest, en één extra
   * verzoek is goedkoper dan een hele klasse verlopen-handtekening-fouten.
   */
  private async partBatch(id: string, from: number): Promise<PresignedPart[]> {
    const item = this.items.get(id)!

    const batch = await getUploadParts(item.slug, item.mediaItemId!, from, partsPerBatch)

    return batch.parts
  }

  private async uploadWeb(id: string, signal: AbortSignal): Promise<void> {
    const item = this.items.get(id)!

    if (item.webUploaded) {
      return
    }

    const target = item.plan!.web!
    const total = item.sizeBytes + item.web!.size

    await putToStorage(target.url, item.web!, target.headers, {
      signal,
      onProgress: (bytes) => {
        this.sample(bytes)
        this.progress(id, (item.sizeBytes + bytes) / total)
      },
    })

    await this.update(id, { webUploaded: true })
  }

  /** Ruwe snelheidsmeting: genoeg voor "nog ± 40 sec", niet meer dan dat. */
  private sample(bytes: number): void {
    const now = Date.now()
    const previous = this.lastSample

    this.lastSample = { at: now, bytes }

    if (!previous || now <= previous.at || bytes < previous.bytes) {
      return
    }

    const observed = ((bytes - previous.bytes) * 1000) / (now - previous.at)

    this.rate = this.rate === null ? observed : this.rate * 0.7 + observed * 0.3
  }
}

/** Hoeveel bytes part N groot is; het laatste part is meestal korter. */
function partBytes(partNumber: number, partSize: number, fileSize: number): number {
  const start = (partNumber - 1) * partSize

  return Math.max(0, Math.min(partSize, fileSize - start))
}

/**
 * De wachtrij van deze tab. De klasse is los geëxporteerd zodat een test een
 * tweede instantie kan maken die dezelfde IndexedDB leest — dat is precies wat
 * "tab sluiten en heropenen" betekent.
 */
export const uploadQueue = new UploadQueue()

/**
 * Zodra er weer netwerk is meteen doorgaan, in plaats van wachten tot de
 * backoff-timer toevallig afloopt. Op een feest gaat de wifi voortdurend even
 * weg en dan is dat verschil merkbaar.
 */
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    void uploadQueue.retryAll()
  })
}
